import {useTranslation} from '@aboutyou/i18n';
import React, {FC} from 'react';
import styled from 'styled-components';
import {COLORS} from '../../../../../constants/colors';
import {SPACING} from '../../../../../constants/sizing';
import {FilterCard} from '../../../../atoms/FilterCard/FilterCard';
import {RadioButton} from '../../../../atoms/RadioButton/RadioButton';
import {RadioGroup} from '../../../../atoms/RadioButton/RadioGroup';
import {HorizontalList} from '../../../HorizontalList/HorizontalList';
import {FilterSingleSelectOptions} from '../../FilterContent/FilterSingleSelectOptions/FilterSingleSelectOptions';
import {SortingOption} from '../../types/SortingOption';
import {FilterFlyout} from '../FilterFlyout';
import {FilterFlyoutFooter} from '../FilterFlyoutFooter';

interface Props {
  activeOptionId: string;
  onSelect: (optionId: string) => void;
  options: Array<SortingOption>;
  isOpen: boolean;
  open: VoidFunction;
  hide: VoidFunction;
  submit: VoidFunction;
  enableFlyoutFilter: boolean;
  showAsRadioGroup?: boolean;
}

const FlyoutFilter = (props: Props) => {
  const {
    options,
    activeOptionId,
    hide,
    open,
    submit,
    isOpen,
    onSelect,
    enableFlyoutFilter,
    showAsRadioGroup,
  } = props;
  const {t} = useTranslation();
  const activeOption = options.find(item => item.id === activeOptionId);

  return (
    <Section data-testid="filterFlyoutSectionSort">
      <StyledFilterCard
        title={t('Sort')}
        onClick={open}
        disableHorizontalPadding={enableFlyoutFilter}
        selectedValues={activeOption?.label}
      >
        <FilterFlyout
          data-testid="filterFlyoutSectionPrice_filterFlyout"
          onClose={hide}
          open={isOpen}
          title={t('Sort')}
          primaryButtonLabel={t('Submit')}
          secondaryButtonLabel={t('Back')}
          footer={FilterFlyoutFooter}
          onPrimaryButtonClick={submit}
        >
          <Wrapper>
            {showAsRadioGroup && (
              <StyledRadioGroup>
                {options.map(({id, label}) => (
                  <RadioButton
                    key={id}
                    name="sort"
                    label={label}
                    value={id}
                    checked={id === activeOptionId}
                    onChange={() => onSelect(id)}
                    data-testid={`sort-filter-${id}`}
                  />
                ))}
              </StyledRadioGroup>
            )}
            {!showAsRadioGroup && (
              <List
                dataTestId="filterFlyoutSort"
                activeOptionId={activeOptionId}
                onSelect={onSelect}
                options={options}
                Wrapper={HorizontalList}
                $enableFlyoutFilter={enableFlyoutFilter}
              />
            )}
          </Wrapper>
        </FilterFlyout>
      </StyledFilterCard>
    </Section>
  );
};

const StandardFilter = (props: Props) => {
  const {t} = useTranslation();
  const {
    activeOptionId,
    onSelect,
    options,
    enableFlyoutFilter,
    showAsRadioGroup,
  } = props;
  return (
    <div data-testid="filterFlyoutSectionSort">
      <FilterCard
        title={showAsRadioGroup ? '' : t('Sort')}
        disableHorizontalPadding={showAsRadioGroup}
      >
        {showAsRadioGroup && (
          <StyledRadioGroup>
            {options.map(({id, label}) => (
              <RadioButton
                key={id}
                name="sort"
                label={label}
                value={id}
                checked={id === activeOptionId}
                onChange={() => onSelect(id)}
                data-testid={`sort-filter-${id}`}
              />
            ))}
          </StyledRadioGroup>
        )}
        {!showAsRadioGroup && (
          <List
            dataTestId="filterFlyoutSort"
            activeOptionId={activeOptionId}
            onSelect={onSelect}
            options={options}
            Wrapper={HorizontalList}
            $enableFlyoutFilter={enableFlyoutFilter}
          />
        )}
      </FilterCard>
    </div>
  );
};

export const FilterFlyoutSort: FC<Props> = props => {
  const {enableFlyoutFilter} = props;

  return enableFlyoutFilter ? (
    <FlyoutFilter {...props} />
  ) : (
    <StandardFilter {...props} />
  );
};

const Wrapper = styled.div`
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};
`;

const Section = styled.div`
  border-bottom: 4px solid ${COLORS.Grey4};
`;

const List = styled(FilterSingleSelectOptions)<{$enableFlyoutFilter?: boolean}>`
  ${props =>
    props.$enableFlyoutFilter ? 'height: 100%;' : 'margin-top: 1.375rem;'}
`;

const StyledRadioGroup = styled(RadioGroup)`
  flex-direction: column;
  gap: 20px;
`;

const StyledFilterCard = styled(FilterCard)`
  border-bottom: unset;
`;
